import React from 'react';
import {
  Box, ButtonBase, Container, Grid, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DisplayStringTrans, Trans, useTranslation } from '@gamiphy/react-i18n';
import StarIcon from '@mui/icons-material/Star';
import { LoyaltyLevel } from '../../types';
import { useLoyaltyPageContext } from '../../context';

type Props = {
  levels: LoyaltyLevel[],
  mobileBenefitsData: { [key: string]: string[] };
}

const MobileDisplay: React.FC<Props> = ({
  levels,
  mobileBenefitsData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedLevel, setSelectedLevel] = React.useState<LoyaltyLevel | undefined>(levels[0]);
  const currentCashBack = React.useMemo(() => {
    if (!selectedLevel) {
      return 0;
    }
    return Math.round(selectedLevel.cash * 100);
  }, [selectedLevel]);

  const { cashbackProgram: { isCashbackProgram, cashbackPercentage }, isCashbackEnabled } = useLoyaltyPageContext();

  return (
    <Container>
      <Typography
        variant="body2"
        gutterBottom
        color="#4F4F4F"
        sx={{
          textAlign: 'center',
          py: 2.5,
        }}
      >
        {t('loyaltyPage.levels.benefits', 'Benefits')}
      </Typography>
      <Grid container wrap="nowrap" justifyContent="flex-start">
        {levels.map((level: LoyaltyLevel) => {
          const isSelected = level.id === selectedLevel?.id;
          return (
            <Grid key={level.index} mr="auto">
              <ButtonBase
                sx={{
                  display: 'inline-block',
                  pb: 0.8,
                  color: '#1A191C',
                  fontSize: '1.4rem',
                  fontWeight: isSelected ? 700 : 600,
                  borderBottom: '3px solid transparent',
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                }}
                onClick={() => setSelectedLevel(level)}
              >
                <DisplayStringTrans displayString={level.name} />
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>

      <Box py={2}>
        <Typography
          variant="body2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '&>span': {
              color: 'primary.main',
              fontWeight: 700,
            },
          }}
        >
          <StarIcon sx={{ fontSize: 24 }} color="primary" />
          {isCashbackProgram && isCashbackEnabled ? (
            <Trans
              i18nKey="loyaltyPage.cashbackProgram.cashback.description"
              defaults="Make a purchase and get <span>{{cashbackPercentage}}%</span> cashback"
              values={{
                currentCashBack,
                cashbackPercentage,
              }}
              components={{
                span: <span style={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                }}
                />,
              }}
            />
          ) : (
            <Trans
              i18nKey="loyaltyPage.cashback.description"
              defaults="Make a purchase and get <span>{{currentCashBack}}% </span> back in {{,gems}}"
              values={{
                currentCashBack,
              }}
              components={{
                span: <span style={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                }}
                />,
              }}
            />
          )}
        </Typography>
      </Box>
      {
        !!selectedLevel && (
          <>
            {mobileBenefitsData[selectedLevel.id].map((benefit) => (
              <Box py={2} key={`${selectedLevel.id}-${benefit}`}>
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&>span': {
                      color: 'primary.main',
                      fontWeight: 700,
                    },
                  }}
                >
                  <StarIcon sx={{ fontSize: 24 }} color="primary" />
                  {benefit}
                </Typography>
              </Box>
            ))}
          </>
        )
      }
    </Container>
  );
};

export default MobileDisplay;
